import { Form, Input, Select, Space } from 'antd';
import { useMemo } from 'react';
import type { ReactNode } from 'react';

import type { Country } from '../../api/countries';
import { updateCustomerInvoiceIdentity, updateCustomerNames } from '../../api/customers';
import type { CustomerDetail } from '../../api/customers';
import { PermissionCodes } from '../../auth/permissions';
import { useAuth } from '../../auth/useAuth';
import { CustomerEditCard } from './CustomerEditCard';

/** Giá trị của thẻ sửa tên. */
interface NamesFormValues {
  readonly legalName: string;
  readonly shortName: string;
  readonly nameEn: string | null;
}

/** Giá trị của thẻ sửa thông tin hóa đơn. */
interface InvoiceFormValues {
  readonly taxCode: string | null;
  readonly invoiceAddress: string | null;
  readonly countryId: string;
}

/** Tham số của phần định danh. */
export interface CustomerIdentitySectionProps {
  /** Khách hàng đang xem. */
  readonly customer: CustomerDetail;

  /** Danh mục quốc gia, đã nạp sẵn bởi trang chi tiết. */
  readonly countries: readonly Country[];

  /** Nhận khách hàng sau khi sửa. */
  readonly onSaved: (updated: CustomerDetail) => void;
}

/**
 * Hai thẻ về định danh của khách hàng: tên, và thông tin in trên hóa đơn.
 *
 * **Tách thành hai thẻ với hai quyền khác nhau là điểm quan trọng nhất của trang này.** Sửa tên gọi
 * tắt là việc thường ngày của người làm việc với khách. Sửa mã số thuế thì khác: nó được in trên hóa
 * đơn, sai một chữ là hóa đơn không hợp lệ, và phát hiện điều đó lúc quyết toán thuế là quá muộn.
 * Hai việc có hậu quả khác nhau tới mức đó thì không thể chung một quyền.
 */
export function CustomerIdentitySection({
  customer,
  countries,
  onSaved,
}: CustomerIdentitySectionProps): ReactNode {
  const { hasPermission } = useAuth();

  const canUpdateNames = hasPermission(PermissionCodes.CustomerUpdate);
  const canUpdateInvoiceIdentity = hasPermission(
    PermissionCodes.CustomerUpdateInvoiceIdentity,
  );

  const countryOptions = countries.map((country) => ({
    value: country.id,
    label: `${country.name} (${country.code})`,
  }));

  /*
    Giữ cùng một đối tượng giá trị ban đầu qua các lần vẽ, chỉ dựng lại khi dữ liệu từ máy chủ đổi.

    Đây KHÔNG phải tối ưu hiệu năng. Thẻ sửa điền lại biểu mẫu mỗi khi giá trị ban đầu đổi, và nếu
    đối tượng đó được tạo mới ở mỗi lần vẽ thì nó "đổi" liên tục — mọi lần trang cha vẽ lại, kể cả
    khi người dùng chỉ mở một hộp thoại khác, sẽ ghi đè lên những gì họ đang gõ.
  */
  const nameValues = useMemo<NamesFormValues>(
    () => ({
      legalName: customer.legalName,
      shortName: customer.shortName,
      nameEn: customer.nameEn,
    }),
    [customer],
  );

  const invoiceValues = useMemo<InvoiceFormValues>(
    () => ({
      taxCode: customer.taxCode,
      invoiceAddress: customer.invoiceAddress,
      countryId: customer.countryId,
    }),
    [customer],
  );

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <CustomerEditCard<NamesFormValues>
        title="Tên khách hàng"
        canEdit={canUpdateNames}
        initialValues={nameValues}
        onSave={async (values) => await updateCustomerNames(customer.id, values)}
        onSaved={onSaved}
      >
        <Form.Item
          name="legalName"
          label="Tên pháp lý đầy đủ"
          extra={
            customer.requiresTaxCode
              ? 'ĐƯỢC IN TRÊN HÓA ĐƠN. Phải khớp từng chữ với giấy chứng nhận đăng ký doanh nghiệp, '
                + 'kể cả phần Công ty TNHH hay Công ty Cổ phần.'
              : 'Tên đầy đủ của nút này.'
          }
          rules={[{ required: true, message: 'Chưa nhập tên pháp lý.' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="shortName"
          label="Tên gọi tắt"
          extra="Hiện trên danh sách và ô chọn."
          rules={[{ required: true, message: 'Chưa nhập tên gọi tắt.' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item name="nameEn" label="Tên tiếng Anh">
          <Input />
        </Form.Item>
      </CustomerEditCard>

      <CustomerEditCard<InvoiceFormValues>
        title="Thông tin in trên hóa đơn"
        description={
          customer.requiresTaxCode
            ? 'Cấp pháp nhân bắt buộc có cả mã số thuế và địa chỉ hóa đơn. Ba giá trị dưới đây là một '
              + 'bộ và được sửa cùng nhau, vì cả ba đều xuất hiện trên hóa đơn — sửa lẻ một trường dễ '
              + 'để lại bộ thông tin không nhất quán.'
            : 'Cấp này không phải người nộp thuế nên mã số thuế và địa chỉ hóa đơn thường để trống. '
              + 'Hóa đơn được xuất ở cấp pháp nhân.'
        }
        canEdit={canUpdateInvoiceIdentity}
        initialValues={invoiceValues}
        onSave={async (values) => await updateCustomerInvoiceIdentity(customer.id, values)}
        onSaved={onSaved}
      >
        <Form.Item
          name="taxCode"
          label="Mã số thuế"
          extra="Mười chữ số, hoặc mười chữ số kèm ba số đơn vị phụ thuộc. Ví dụ: 0111193780, 0111193780-001."
          rules={
            customer.requiresTaxCode
              ? [{ required: true, message: 'Cấp pháp nhân bắt buộc có mã số thuế.' }]
              : []
          }
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="invoiceAddress"
          label="Địa chỉ hóa đơn"
          rules={
            customer.requiresTaxCode
              ? [{ required: true, message: 'Cấp pháp nhân bắt buộc có địa chỉ hóa đơn.' }]
              : []
          }
        >
          <Input.TextArea rows={2} />
        </Form.Item>

        <Form.Item
          name="countryId"
          label="Quốc gia"
          extra="Quyết định việc có phải tính thuế nhà thầu nước ngoài, và là xuất xứ trên giấy tờ hải quan."
          rules={[{ required: true, message: 'Chưa chọn quốc gia.' }]}
        >
          <Select<string> showSearch optionFilterProp="label" options={countryOptions} />
        </Form.Item>
      </CustomerEditCard>
    </Space>
  );
}
